import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { supabase } from "../supabaseClient";
import { useAuth } from "../context/AuthContext";
import Header from "../components/Header";

const LOGO_URL = "https://cdn.outfall.website/Images/Logo/OutFall%20Logo%20Orange.png";

export default function CareersView() {
  const { slug } = useParams();
  const { session } = useAuth();

  const [career, setCareer] = useState(null);
  const [fields, setFields] = useState([]);
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [loggingIn, setLoggingIn] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    loadCareer();
  }, [slug]);

  async function loadCareer() {
    setLoading(true);

    const { data: careerData, error: careerError } = await supabase
      .from("careers")
      .select("*")
      .eq("slug", slug)
      .eq("enabled", true)
      .single();

    if (careerError || !careerData) {
      setCareer(null);
      setFields([]);
      setLoading(false);
      return;
    }

    setCareer(careerData);

    const { data: fieldData, error: fieldError } = await supabase
      .from("career_fields")
      .select("*")
      .eq("career_id", careerData.id)
      .eq("enabled", true)
      .order("sort_order", { ascending: true });

    if (!fieldError) {
      setFields(fieldData || []);
    }

    setLoading(false);
  }

  function updateField(fieldKey, value) {
    setFormData((prev) => ({ ...prev, [fieldKey]: value }));
  }

  function getApplicantEmail(data) {
    return (
      data.email || data.Email || data.email_address || data.Email_Address ||
      data["email-address"] || data["Email Address"] || data["Email address"] ||
      data.contact_email || data.Contact_Email || ""
    );
  }

  function getApplicantName(data) {
    return (
      data.full_name || data.Full_Name || data["full-name"] || data["Full Name"] ||
      data.name || data.Name || "Applicant"
    );
  }

  async function handleDiscordLogin() {
    setLoggingIn(true);
    await supabase.auth.signInWithOAuth({
      provider: "discord",
      options: { redirectTo: window.location.href },
    });
  }

  async function uploadFile(field, file) {
    if (!file || !career) return null;

    const safeName = file.name.replace(/[^a-zA-Z0-9.-]/g, "_");
    const filePath = `career-applications/${career.id}/${Date.now()}-${safeName}`;

    const { error } = await supabase.storage
      .from("applications")
      .upload(filePath, file);

    if (error) throw error;

    const { data } = supabase.storage.from("applications").getPublicUrl(filePath);
    return { name: file.name, path: filePath, url: data.publicUrl };
  }

  async function submitApplication(e) {
    e.preventDefault();
    setSubmitting(true);
    setMessage(null);

    try {
      if (!career) throw new Error("Career role could not be found.");

      const discordIdentity = session?.user?.identities?.find(i => i.provider === "discord");
      const discordId = discordIdentity?.id || session?.user?.user_metadata?.provider_id;
      const discordUsername = session?.user?.user_metadata?.full_name || session?.user?.user_metadata?.name;

      const finalData = {};
      for (const field of fields) {
        const value = formData[field.field_key];
        if (field.required && !value) throw new Error(`${field.label} is required.`);
        if (field.field_type === "file") {
          finalData[field.field_key] = await uploadFile(field, value);
        } else {
          finalData[field.field_key] = value || "";
        }
      }

      const { error } = await supabase.from("career_applications").insert({
        career_id: career.id,
        applicant_data: finalData,
        status: "submitted",
        discord_id: discordId || null,
        discord_username: discordUsername || null,
      });

      if (error) throw error;

      const applicantEmail = getApplicantEmail(finalData);
      const applicantName = getApplicantName(finalData);

      if (applicantEmail) {
        await fetch("/api/send-career-status", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            email: applicantEmail,
            applicantName,
            status: "submitted",
            roleTitle: career.title || "Outfall Careers",
            logoUrl: LOGO_URL,
            bannerUrl: "",
          }),
        });
      }

      // Send Discord DM confirmation
      if (discordId) {
        await fetch("/api/send-discord-dm", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            discord_id: discordId,
            discord_username: discordUsername,
            status: "submitted",
            role_title: career.title,
            secret: process.env.REACT_APP_DM_WEBHOOK_SECRET,
          }),
        });
      }

      setFormData({});
      setMessage({
        type: "success",
        text: "Application submitted! Check your Discord DMs for a confirmation.",
      });
    } catch (err) {
      setMessage({ type: "error", text: err.message || "Failed to submit application." });
    }

    setSubmitting(false);
  }

  function renderField(field) {
    const baseClass =
      "w-full rounded-xl border border-[#ffb56f]/30 bg-black/50 px-4 py-3 font-bold text-white outline-none placeholder:text-white/40 focus:border-[#ffd978]";

    if (field.field_type === "textarea") {
      return (
        <textarea
          className={`${baseClass} min-h-[140px] resize-y`}
          placeholder={field.placeholder || ""}
          required={field.required}
          value={formData[field.field_key] || ""}
          onChange={(e) => updateField(field.field_key, e.target.value)}
        />
      );
    }

    if (field.field_type === "file") {
      const selectedFile = formData[field.field_key];
      return (
        <div className="flex flex-wrap items-center gap-3">
          <label className="rounded-full bg-[#ffd978] px-5 py-2 text-sm font-black uppercase text-black shadow-[0_5px_0_#c79600] transition hover:-translate-y-0.5 hover:bg-[#ffe897] active:translate-y-1 active:shadow-none">
            Choose File
            <input
              className="hidden"
              type="file"
              required={field.required}
              onChange={(e) => updateField(field.field_key, e.target.files[0])}
            />
          </label>
          <span className="text-sm font-bold text-white/70">
            {selectedFile ? selectedFile.name : "No file selected"}
          </span>
        </div>
      );
    }

    return (
      <input
        className={baseClass}
        type={field.field_type}
        placeholder={field.placeholder || ""}
        required={field.required}
        value={formData[field.field_key] || ""}
        onChange={(e) => updateField(field.field_key, e.target.value)}
      />
    );
  }

  if (loading) {
    return (
      <main className="min-h-screen bg-[#050505] text-white">
        <Header />
        <section className="px-6 pt-36 md:px-[7%]">
          <p className="text-xl font-black">Loading role...</p>
        </section>
      </main>
    );
  }

  if (!career) {
    return (
      <main className="min-h-screen bg-[#050505] text-white">
        <Header />
        <section className="px-6 pt-36 md:px-[7%]">
          <h1 className="outfall-heading text-7xl text-[#ffd978]">Role Not Found</h1>
          <Link to="/careers" className="outfall-button mt-6">Back To Careers</Link>
        </section>
      </main>
    );
  }

  const discordUser = session?.user?.user_metadata;

  return (
    <main className="min-h-screen bg-[#050505] text-white">
      <Header />

      <section className="px-6 pt-36 pb-16 md:px-[7%]">
        <Link to="/careers" className="font-black uppercase text-[#ffb56f] hover:text-[#ffd978]">
          ← Back To Careers
        </Link>

        <p className="mt-8 font-black uppercase text-[#ffb56f]">
          {career.department || "Outfall Team"}
        </p>

        <h1 className="outfall-heading text-[clamp(58px,9vw,120px)] leading-none text-[#ffd978] drop-shadow-[0_0_18px_#ff8f6b]">
          {career.title}
        </h1>

        <div className="mt-5 flex flex-wrap gap-3">
          {career.location && (
            <span className="rounded-full bg-[#ffb56f] px-4 py-2 font-black text-black">
              {career.location}
            </span>
          )}
          {career.type && (
            <span className="rounded-full bg-[#ffd978] px-4 py-2 font-black text-black">
              {career.type}
            </span>
          )}
        </div>

        <p className="mt-8 max-w-4xl whitespace-pre-line text-xl font-bold leading-relaxed text-white">
          {career.description}
        </p>
      </section>

      <section className="px-6 pb-24 md:px-[7%]">
        <div className="max-w-4xl rounded-[28px] border-4 border-[#ffd978] bg-black/60 p-6 shadow-[0_0_35px_rgba(255,143,107,0.35)] md:p-10">
          <h2 className="outfall-heading text-6xl text-[#ffd978]">Apply Now</h2>

          {!session ? (
            <div className="mt-8">
              <p className="mb-6 font-bold text-white/70">
                Sign in with Discord to apply. Your Discord account will be linked to your application so we can update you directly.
              </p>
              <button
                onClick={handleDiscordLogin}
                disabled={loggingIn}
                className="flex items-center gap-3 rounded-xl bg-[#5865F2] px-6 py-4 font-black text-white transition hover:bg-[#4752C4] disabled:opacity-50"
              >
                <svg width="24" height="18" viewBox="0 0 24 18" fill="currentColor">
                  <path d="M20.317 1.492a19.825 19.825 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.294 18.294 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 1.492a.07.07 0 0 0-.032.027C.533 6.093-.32 10.555.099 14.961a.08.08 0 0 0 .031.055 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.3 12.3 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.442a.061.061 0 0 0-.031-.028z"/>
                </svg>
                {loggingIn ? "Redirecting…" : "Continue with Discord"}
              </button>
            </div>
          ) : (
            <>
              <div className="mt-4 flex items-center gap-3">
                {discordUser?.avatar_url && (
                  <img src={discordUser.avatar_url} alt="" className="h-8 w-8 rounded-full" />
                )}
                <p className="font-bold text-white/60">
                  Applying as <span className="font-black text-[#ffd978]">{discordUser?.full_name || discordUser?.name || "you"}</span>
                </p>
                <button
                  type="button"
                  onClick={() => supabase.auth.signOut()}
                  className="ml-auto text-sm font-black uppercase text-white/30 hover:text-white/60"
                >
                  Sign out
                </button>
              </div>

              <form onSubmit={submitApplication}>
                <div className="mt-8 grid gap-6">
                  {fields.length === 0 && (
                    <p className="font-black text-white">No application fields have been configured for this role.</p>
                  )}
                  {fields.map((field) => (
                    <label key={field.id} className="grid gap-2">
                      <span className="font-black uppercase text-[#ffb56f]">
                        {field.label}
                        {field.required && <span className="text-[#ffd978]"> *</span>}
                      </span>
                      {renderField(field)}
                    </label>
                  ))}
                </div>

                {message && (
                  <div className={`mt-6 rounded-xl px-4 py-3 font-black ${message.type === "success" ? "bg-green-500 text-black" : "bg-red-500 text-white"}`}>
                    {message.text}
                  </div>
                )}

                <div className="mt-8 flex flex-wrap items-center gap-4">
                  <button
                    type="submit"
                    disabled={submitting || fields.length === 0}
                    className="outfall-button disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {submitting ? "Submitting..." : "Submit Application"}
                  </button>
                  <Link to="/my-application" className="font-black uppercase text-[#ffb56f] hover:text-[#ffd978] text-sm">
                    Check existing application →
                  </Link>
                </div>
              </form>
            </>
          )}
        </div>
      </section>
    </main>
  );
}
